// The site's brand (name, contact details, address, socials, colours…).
//
// Where it comes from, in order:
//   1. src/brand/defaultBrand.json — built-in defaults (this site's own brand).
//   2. window.__BRAND__ — the tenant's saved settings, baked into every HTML
//      page by scripts/generate-seo.mjs at build time, so the right brand shows
//      on first paint with no flicker.
//   3. The `tenants` table in Supabase — re-read after load, so brand edits made
//      in the admin panel show up without waiting for a rebuild.
// Which tenant a build belongs to is set with REACT_APP_TENANT (default: the
// slug in defaultBrand.json).

import { createContext, useContext, useEffect, useState } from "react";
import defaultBrand from "./defaultBrand.json";
import { resolveBrand } from "./mergeBrand";
import { configureSeo } from "../lib/seo";
import { supabase } from "../lib/supabase";

export const TENANT_SLUG = process.env.REACT_APP_TENANT || defaultBrand.slug;
// Built-in assets (logo, og-image.jpg, favicon) are this site's own; other tenants need their own.
export const IS_DEFAULT_TENANT = TENANT_SLUG === defaultBrand.slug;

function initialBrand() {
  const baked = typeof window !== "undefined" ? window.__BRAND__ : null;
  return resolveBrand(defaultBrand, TENANT_SLUG, baked && baked.slug === TENANT_SLUG ? baked : null);
}

/* Push brand colours into the CSS variables the stylesheets use. Only colours
   the tenant actually set are applied; everything else keeps the theme default. */
function applyTheme(brand) {
  const root = document.documentElement;
  const { primary, accent } = brand.colors || {};
  const set = (name, value) => (value ? root.style.setProperty(name, value) : root.style.removeProperty(name));
  set("--primary-500", primary);
  set("--primary-600", primary);
  set("--primary-700", primary);
  set("--accent-500", accent);
  set("--accent-600", accent);
}

// Configure SEO before the first render so titles/canonicals use the right brand.
const startingBrand = initialBrand();
configureSeo(startingBrand);

const BrandContext = createContext(startingBrand);

/* Fetch the latest saved brand for this tenant (null if none / table missing). */
export async function fetchTenantBrand(slug = TENANT_SLUG) {
  const { data, error } = await supabase.from("tenants").select("brand").eq("slug", slug).maybeSingle();
  if (error || !data) return null;
  return data.brand || {};
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(startingBrand);

  useEffect(() => {
    applyTheme(brand);
  }, [brand]);

  useEffect(() => {
    let cancelled = false;
    fetchTenantBrand()
      .then((saved) => {
        if (cancelled || !saved) return;
        const next = resolveBrand(defaultBrand, TENANT_SLUG, saved);
        configureSeo(next);
        setBrand(next);
      })
      .catch(() => {}); // keep the baked-in brand if Supabase is unreachable
    return () => {
      cancelled = true;
    };
  }, []);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}

/* Helpers for formatting brand contact details. */
export const telHref = (phone) => `tel:${String(phone || "").replace(/[^0-9+]/g, "")}`;
export const whatsappHref = (number, text) =>
  `https://wa.me/${String(number || "").replace(/[^0-9]/g, "")}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
export const addressLine = (address = {}) =>
  [address.streetAddress, address.addressLocality, address.postalCode].filter(Boolean).join(", ");
